import React, { useCallback, useEffect, useRef, useState } from 'react';
import Hls from 'hls.js';
import { Camera, Radio, RefreshCw } from 'lucide-react';
import { useTabActivity } from '../../contexts/TabActivityContext';
import { useRealtimeMedia } from '../../hooks/useRealtimeMedia';
import { apiService, RealtimeFeed } from '../../services/api';
import { getRealtimeErrorMessage } from '../../features/realtime/errors';
import { ACTIVE_REALTIME_STATUSES } from '../../features/realtime/presentation';
import RealtimeMediaTime from './RealtimeMediaTime';
import RealtimeRecordingPlayer from './RealtimeRecordingPlayer';
import '../SegmentedPlayer.css';
import './LiveFeedPlayer.css';

const readLiveWindow = (video: HTMLVideoElement, hls: Hls | null) => {
  if (!video.seekable.length) return null;
  const fragments = hls?.latestLevelDetails?.fragments || [];
  const start = Math.max(video.seekable.start(0), fragments[0]?.start ?? -Infinity);
  const end = Math.min(video.seekable.end(video.seekable.length - 1), fragments[fragments.length - 1]?.end ?? Infinity);
  if (!Number.isFinite(start) || !Number.isFinite(end) || end <= start) return null;
  const timestampAt = (position: number) => {
    const fragment = fragments.find(item => position >= item.start && position < item.end)
      || (position === fragments[fragments.length - 1]?.end ? fragments[fragments.length - 1] : undefined);
    return fragment?.programDateTime != null ? fragment.programDateTime + (position - fragment.start) * 1000 : null;
  };
  const position = Math.max(start, Math.min(end, video.currentTime));
  return { start, end, position, playbackPosition: video.currentTime, startTimestamp: timestampAt(start), endTimestamp: timestampAt(end), positionTimestamp: timestampAt(video.currentTime) };
};

export default function LiveFeedPlayer({ feed, compact = false }: { feed: RealtimeFeed; compact?: boolean }) {
  return feed.input_mode === 'video_test' && feed.test_video_id && !compact
    ? <RealtimeRecordingPlayer key={`${feed.feed_id}:${feed.runtime_id}`} feed={feed} />
    : <LiveCameraPlayer feed={feed} compact={compact} />;
}

function LiveCameraPlayer({ feed, compact }: { feed: RealtimeFeed; compact: boolean }) {
  const { isActive } = useTabActivity();
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const hlsRef = useRef<Hls | null>(null);
  const isActiveRef = useRef(isActive);
  isActiveRef.current = isActive;
  const isLive = feed.status === 'live' && Boolean(feed.playlist_url);
  const poster = useRealtimeMedia(isLive ? null : feed.latest_event?.thumbnail_url || null, feed.latest_event?.thumbnail_state);
  const [failedPoster, setFailedPoster] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);
  const [attempt, setAttempt] = useState(0);
  const [liveWindow, setLiveWindow] = useState<ReturnType<typeof readLiveWindow>>(null);
  const updateTimeline = useCallback(() => {
    if (!compact && videoRef.current) setLiveWindow(readLiveWindow(videoRef.current, hlsRef.current));
  }, [compact]);

  useEffect(() => {
    const video = videoRef.current;
    if (!video || feed.status !== 'live' || !feed.playlist_url) return undefined;
    let disposed = false;
    let hls: Hls | null = null;
    setError(''); setLoading(true); setLiveWindow(null);

    (async () => {
      try {
        const playback = await apiService.getRealtimePlaybackConfig(feed.playlist_url || '');
        if (disposed) return;
        if (!Hls.isSupported()) {
          setError('Live preview is unavailable in this browser.');
          setLoading(false);
          return;
        }
        hls = new Hls({
          autoStartLoad: isActiveRef.current,
          lowLatencyMode: true,
          liveSyncDuration: 1.5,
          // Manual DVR seeks remain at normal speed inside the retained window.
          liveMaxLatencyDuration: Infinity,
          maxLiveSyncPlaybackRate: 1,
          backBufferLength: 6,
          maxBufferLength: 4,
          xhrSetup: xhr => xhr.setRequestHeader('Authorization', playback.authorization),
        });
        hlsRef.current = hls;
        hls.on(Hls.Events.LEVEL_UPDATED, updateTimeline);
        hls.on(Hls.Events.ERROR, (_event, data) => {
          if (!disposed && data.fatal) { setError('Live preview was interrupted.'); setLoading(false); }
        });
        hls.on(Hls.Events.MANIFEST_PARSED, () => {
          if (disposed) return;
          setLoading(false);
          updateTimeline();
          if (!isActiveRef.current) return;
          video.muted = true;
          const edge = hls?.liveSyncPosition;
          if (typeof edge === 'number') video.currentTime = edge;
          void video.play().catch(() => undefined);
        });
        hls.loadSource(playback.url);
        hls.attachMedia(video);
      } catch (playbackError) {
        if (!disposed) { setError(getRealtimeErrorMessage(playbackError, 'Unable to open preview.')); setLoading(false); }
      }
    })();

    return () => {
      disposed = true;
      hls?.destroy();
      if (hlsRef.current === hls) hlsRef.current = null;
      video.pause();
    };
  }, [feed.feed_id, feed.playlist_url, feed.runtime_id, feed.status, attempt, updateTimeline]);

  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;
    if (!isActive) { hlsRef.current?.stopLoad(); video.pause(); }
    else if (hlsRef.current) { hlsRef.current.startLoad(); void video.play().catch(() => undefined); }
  }, [isActive]);

  const seek = (position: number) => {
    const video = videoRef.current;
    const available = video && readLiveWindow(video, hlsRef.current);
    if (!video || !available) return;
    video.currentTime = Math.max(available.start, Math.min(available.end, position));
    updateTimeline();
  };
  const goLive = () => {
    const video = videoRef.current;
    if (!video || !liveWindow) return;
    seek(hlsRef.current?.liveSyncPosition ?? liveWindow.end);
    if (isActive) void video.play().catch(() => undefined);
  };

  const shellClass = `realtime-video-shell realtime-live-player ${compact ? 'realtime-video-compact' : ''}`;
  if (!isLive) {
    if (poster.url && poster.url !== failedPoster) return <div className={`${shellClass} realtime-live-player--poster`}>
      <img src={poster.url} alt={`Latest observation from ${feed.name}`} onError={() => setFailedPoster(poster.url)} />
    </div>;
    const connecting = ACTIVE_REALTIME_STATUSES.has(feed.status);
    const label = feed.input_mode === 'video_test' && connecting ? 'Preparing test…' : feed.status === 'reconnecting' ? 'Reconnecting…' : connecting ? 'Connecting…'
      : feed.status === 'needs_credentials' ? 'Camera sign-in required'
        : feed.status === 'failed' ? 'Unable to connect'
          : poster.error || (poster.url && poster.url === failedPoster) ? 'Preview unavailable' : 'No preview yet';
    return <div className={`realtime-player-placeholder realtime-live-player ${compact ? 'realtime-player-compact' : ''}`}>
      {connecting || poster.loading ? <RefreshCw size={compact ? 20 : 28} className="spinning" /> : <Camera size={compact ? 20 : 28} />}
      <span>{poster.loading ? 'Loading preview…' : label}</span>
    </div>;
  }

  return <><div className={shellClass}>
    <video ref={videoRef} muted playsInline controls={!compact} aria-label={`Live feed ${feed.name}`}
      onTimeUpdate={updateTimeline} onProgress={updateTimeline} onLoadedMetadata={updateTimeline} onDurationChange={updateTimeline} onSeeked={updateTimeline} />
    {!loading && !error && <div className="realtime-live-overlay"><span><Radio size={12} />Live feed</span></div>}
    {loading && <div className="segmented-player-loading" role="status" aria-label="Loading live preview"><RefreshCw size={compact ? 20 : 28} className="spinning" /><span>Loading preview…</span></div>}
    {error && <div className="realtime-player-error" role="alert"><span>{error}</span><button type="button" className="action-button" onClick={event => { event.stopPropagation(); setAttempt(current => current + 1); }}>Retry</button></div>}
  </div>
    {!compact && !error && liveWindow && <section className="realtime-live-timeline" aria-label="Available live window" onClick={event => event.stopPropagation()}>
      <div className="realtime-live-timeline-heading"><span>Available live window</span><button type="button" className="action-button" onClick={goLive}><Radio size={12} />Go live</button></div>
      <input type="range" aria-label="Seek within live window" min={liveWindow.start} max={liveWindow.end} step="0.1" value={liveWindow.position}
        aria-valuetext={liveWindow.positionTimestamp !== null ? new Date(liveWindow.positionTimestamp).toISOString() : `${liveWindow.playbackPosition.toFixed(1)} seconds`}
        onChange={event => seek(Number(event.target.value))} />
      <dl className="realtime-live-timestamps">
        <div><dt>Start</dt><dd>{liveWindow.startTimestamp !== null ? <RealtimeMediaTime value={liveWindow.startTimestamp} /> : `${liveWindow.start.toFixed(1)}s`}</dd></div>
        <div><dt>Playing</dt><dd>{liveWindow.positionTimestamp !== null ? <RealtimeMediaTime value={liveWindow.positionTimestamp} /> : `${liveWindow.playbackPosition.toFixed(1)}s`}</dd></div>
        <div><dt>End</dt><dd>{liveWindow.endTimestamp !== null ? <RealtimeMediaTime value={liveWindow.endTimestamp} /> : `${liveWindow.end.toFixed(1)}s`}</dd></div>
      </dl>
    </section>}
  </>;
}
